
import isTouchDevice from 'is-touch-device'
import PinchZoom from 'pinch-zoom-js'
import { useResizeStore } from '~/store/resize'

export default {
  name: 'ProductPhotoSlider',
  props: {
    /**
     * Список картинок
     * ['image1', 'image2', 'image3']
     */
    photo: {
      type: Array,
      default() {
        return [require('@/assets/images/empty-product.png')]
      },
    },
    /**
     * Альтернативный текст для изображений
     */
    alt: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    zoomWrapper: {
      width: 0,
      height: 0,
    },
    zoomDevice: 'product-slider_touch-zoom',
    zoomActive: true,
    zoomTouch: false,
    selectorWidth: null,
    slideWidth: 0,
    minFrameOffset: null,
    transformProperty: 'translate3d(0, 0, 0);',
    transition: true,
    cursor: 'pointer',
    offset: 0,
    duration: 200,
    pointerDown: false,
    multipleDrag: true,
    threshold: 20,
    currentSlide: 0,
    perPage: 1,
    loop: false,
    maxIndexInNoneLoop: null,
    drag: {
      startX: 0,
      endX: 0,
      preventClick: false,
    },
    options: {
      index: 0,
      history: false,
    },
    isOpen: false,
    items: [],
  }),
  computed: {
    styleHandler() {
      return `transform: ${this.transformProperty} ${
        this.transition ? ' transition:  all 200ms ease-out;' : ' transition:  all 0ms ease-out;'
      }`
    },
    styleZoomWrapper() {
      return { width: this.zoomWrapper.width, height: this.zoomWrapper.height }
    },
  },
  created() {
    this.resizeStore = useResizeStore(this.$pinia)
  },
  mounted() {
    this.calculateImportantVariables()
    this.buildSliderFrame()
    if (isTouchDevice()) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.initTouchZoom()
        })
      })
    }

    this.unsubscribe = this.resizeStore.$onAction((mutation) => {
      if (mutation.name === 'setIsMobile') {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.resizeHandler()
          })
        })
      }
    })
  },
  destroyed() {
    if (this.unsubscribe)
      this.unsubscribe()
  },
  methods: {
    calculateImportantVariables() {
      this.zoomActive = !isTouchDevice()
      this.selectorWidth = this.$refs?.selector?.offsetWidth
      this.slideWidth = this.selectorWidth
      this.maxIndexInNoneLoop = this.photo.length - Math.floor(this.selectorWidth / this.slideWidth)
    },
    buildSliderFrame() {
      this.sliderFrameWidth = this.slideWidth * this.photo.length
      this.minFrameOffset = (this.sliderFrameWidth - this.selectorWidth) * -1
      this.enableTransition()
    },
    resizeHandler() {
      this.calculateImportantVariables()
      this.buildSliderFrame()
      if (isTouchDevice())
        this.initTouchZoom()
      this.pointerDown = false
      this.drag = {
        startX: 0,
        endX: 0,
        preventClick: false,
      }
      if (this.photo.length <= 1)
        this.offset = this.currentSlide = 0
      else
        this.offset = -1 * this.currentSlide * this.slideWidth

      this.transformProperty = `translate3d(${this.offset}px, 0, 0);`
    },
    touchstartHandler(e) {
      if (this.photo.length <= 1 || this.isActiveHandler())
        return null

      this.pointerDown = true
      this.drag.startX = e.changedTouches[0].pageX
    },
    touchendHandler() {
      if (this.photo.length <= 1 || this.isActiveHandler())
        return null

      this.pointerDown = false
      this.enableTransition()
      if (this.drag.endX)
        this.updateAfterDrag()
    },
    touchmoveHandler(e) {
      if (this.photo.length <= 1 || this.isActiveHandler())
        return null

      if (this.pointerDown) {
        this.drag.endX = e.changedTouches[0].pageX
        this.moveHandler()
      }
    },
    mouseupHandler(e) {
      if (this.photo.length <= 1 || this.isActiveHandler())
        return null

      this.cursor = 'pointer'
      this.pointerDown = false
      this.enableTransition()

      const delta = 5 // устанавливает, со сдвига насколько пикселей должен сработать свайп, а не щелчок
      const difference = Math.abs(e.pageX - this.drag.startX)
      if (difference < delta)
        this.showPhotoSwipe(this.currentSlide)

      if (this.drag.endX)
        this.updateAfterDrag()

      this.clearDrag()
    },
    mousedownHandler(e) {
      if (this.photo.length <= 1 || this.isActiveHandler()) {
        this.showPhotoSwipe(this.currentSlide)
        return null
      }
      e.stopPropagation()
      this.cursor = 'grabbing'
      this.pointerDown = true
      this.drag.startX = e.pageX
    },
    mouseenterHandler() {
      document.addEventListener('keydown', this.onActiveKeybordArrows)
    },
    mouseleaveHandler(e) {
      document.removeEventListener('keydown', this.onActiveKeybordArrows)
      if (this.photo.length <= 1 || this.isActiveHandler())
        return null

      if (this.pointerDown) {
        this.cursor = 'grab'
        this.pointerDown = false
        this.drag.endX = e.pageX
        this.drag.preventClick = false
        this.enableTransition()
        this.updateAfterDrag()
        this.clearDrag()
      }
    },
    onActiveKeybordArrows(e) {
      if (e.which === 39)
        this.next()
      else if (e.which === 37)
        this.prev()
    },
    mousemoveHandler(e) {
      if (this.photo.length <= 1 || this.isActiveHandler())
        return null

      if (this.pointerDown) {
        if (e.target.tagName === 'A' || e.target.parentNode.tagName === 'A')
          this.drag.preventClick = true

        this.drag.endX = e.pageX
        this.moveHandler()
      }
    },
    moveHandler() {
      this.disableTransition()

      const frameOffset = this.getFrameOffset(this.currentSlide)
      const dragOffset = this.drag.endX - this.drag.startX
      this.offset = -1 * (frameOffset - dragOffset)
      this.transformProperty = `translate3d(${this.offset}px, 0, 0);`
    },
    clickIconHandler(howManySlides) {
      if (howManySlides > this.currentSlide)
        this.next(howManySlides - this.currentSlide)
      else
        this.prev(this.currentSlide - howManySlides)

      this.$nextTick(() => {
        // Активная превьюшка
        const activePreview = document.querySelector('.product-slider__thumb_active')
        if (activePreview)
          this.smartScrollPreview(activePreview)
      })
    },
    getFrameOffset(currentSlide) {
      if (this.slideWidth) {
        if (!this.loop && currentSlide * this.slideWidth > Math.abs(this.minFrameOffset)) {
          const diff = Math.abs(this.minFrameOffset) % this.slideWidth

          return (currentSlide - 1) * this.slideWidth + diff
        }
        return currentSlide * this.slideWidth
      }
      return currentSlide * (this.selectorWidth / this.perPage)
    },
    clearDrag() {
      this.drag = {
        startX: 0,
        endX: 0,
        preventClick: this.drag.preventClick,
      }
    },
    disableTransition() {
      this.transition = false
    },
    enableTransition() {
      this.transition = true
    },
    updateAfterDrag() {
      const movement = this.drag.endX - this.drag.startX
      const movementDistance = Math.abs(movement)
      const howManySliderToSlide = this.updateAfterDragHowManySlides(movementDistance)

      const slideToNegativeClone = movement > 0 && this.currentSlide - howManySliderToSlide < 0
      const slideToPositiveClone = movement < 0 && this.currentSlide + howManySliderToSlide > this.photo.length - this.perPage

      if (movement > 0 && movementDistance > this.threshold && this.photo.length > this.perPage)
        this.prev(howManySliderToSlide)
      else if (movement < 0 && movementDistance > this.threshold && this.photo.length > this.perPage)
        this.next(howManySliderToSlide)

      this.slideToCurrent(slideToNegativeClone || slideToPositiveClone)
    },
    updateAfterDragHowManySlides(movementDistance) {
      if (this.multipleDrag) {
        return this.slideWidth
          ? Math.ceil(movementDistance / this.slideWidth)
          : Math.ceil(movementDistance / (this.selectorWidth / this.perPage))
      }
      return 1
    },
    next(howManySlides = 1) {
      if (this.photo.length <= this.perPage)
        return null

      const beforeChange = this.currentSlide

      this.currentSlide = Math.min(this.currentSlide + howManySlides, this.photo.length - this.perPage)
      if (beforeChange !== this.currentSlide)
        this.slideToCurrent(this.loop)
    },
    prev(howManySlides = 1) {
      if (this.photo.length <= this.perPage)
        return null

      const beforeChange = this.currentSlide

      this.currentSlide = Math.max(this.currentSlide - howManySlides, 0)

      if (beforeChange !== this.currentSlide)
        this.slideToCurrent(this.loop)
    },
    smartScrollPreview(activePreview) {
      // На телефонах, когда открывают слайдер и закрывают его свайпом, почему то срабатывает этот метод и выкидывается ошибка, поэтому введено это условие
      if (this.$refs?.selectorPreviewWrapper) {
        let y = null
        const scrollTop = this.$refs?.selectorPreviewWrapper.scrollTop // Позиция скролла от верха
        const previewPosition = activePreview.offsetTop - scrollTop // Позиция превьюшки от верха
        const previewWrapperHeight = this.$refs?.selectorPreviewWrapper.offsetHeight // Высота блока с превьюшками
        const previewHeight = activePreview.offsetHeight // Высота превьюшки
        const maxScrollY = this.$refs?.selectorPreviewWrapper.scrollHeight - previewWrapperHeight // Макс. скролл по вертикали
        const scrollSlideLength = 2 // На какое кол-во превьюшек скроллить при переключении слайдов

        if (previewPosition < previewWrapperHeight * 0.25) {
          // если в верхней четверти
          y = scrollTop - previewHeight * scrollSlideLength
          if (y < 0)
            y = 0
        }
        else if (previewPosition > previewWrapperHeight * 0.75) {
          // если в нижней четверти
          y = scrollTop + previewHeight * scrollSlideLength
          if (y > maxScrollY)
            y = maxScrollY
        }

        // если позиция скролла обновилась
        if (y !== null) {
          this.$refs?.selectorPreviewWrapper.scrollTo({
            top: y,
            behavior: 'smooth',
          })
        }
      }
    },
    slideToCurrent(enableTransition, { animation = true } = {}) {
      const currentSlide = this.currentSlide

      let offset = this.slideWidth ? -1 * currentSlide * this.slideWidth : -1 * currentSlide * (this.selectorWidth / this.perPage)

      if (!this.loop && offset < this.minFrameOffset) {
        offset = this.minFrameOffset
        this.currentSlide = this.maxIndexInNoneLoop
      }

      if (enableTransition) {
        this.offset = offset
        this.transformProperty = `translate3d(${this.offset}px, 0, 0);`
      }
      else if (animation) {
        this.offset = offset
        this.transformProperty = `translate3d(${this.offset}px, 0, 0);`
      }
      else {
        this.disableTransition()
        this.offset = offset
        this.transformProperty = `translate3d(${this.offset}px, 0, 0);`
        window.requestAnimationFrame(() => {
          this.enableTransition()
        })
      }
    },
    transitionLock() {
      this.transitionState = true
      setTimeout(() => {
        this.transitionState = false
      }, this.duration)
    },
    isActiveHandler() {
      return this.photo.length < 1
    },
    initTouchZoom() {
      this.zoomTouch = true
      this.setTouchZoomWrapperSize()
      // Без этого условия в сентри постоянно прокидывается ошибка
      if (this.$refs?.zoomWrapper) {
        Array.from(this.$refs.zoomWrapper).forEach(($el) => {
          this.$nextTick(() => {
            new PinchZoom($el, {
              minZoom: 1,
              draggableUnzoomed: false,
              setOffsetsOnce: true,
            })
          })
        })
      }
      this.unsubscribe = this.resizeStore.$onAction((mutation) => {
        if (mutation.name === 'setIsMobile')
          this.setTouchZoomWrapperSize()
      })
    },
    setTouchZoomWrapperSize() {
      this.zoomWrapper.width = `${this.slideWidth}px`
      this.zoomWrapper.height = `${this.slideWidth}px`
    },
    getPhotoSize() {
      const arr = Array.from(this.$refs.selector?.querySelectorAll('img')).map(($el) => {
        return {
          src: $el.src,
          w: parseInt($el.naturalWidth),
          h: parseInt($el.naturalHeight),
        }
      })
      return arr
    },
    showPhotoSwipe(index) {
      if (!this.items.length)
        this.items = this.getPhotoSize()
      this.isOpen = true
      this.$set(this.options, 'index', index)
      this.activeIndex = index
    },
    hidePhotoSwipe(e) {
      this.clickIconHandler(e.getCurrentIndex())
      this.isOpen = false
    },
  },
}
